import React, { useEffect, useRef } from "react";
import Editor, { type Monaco } from "@monaco-editor/react";
import { useTranslation } from "react-i18next";
import { Icon } from "react-icons-kit";
import { ic_clear } from "react-icons-kit/md/ic_clear";
import { MONACO_OPTIONS } from "../schema-builder/editorConfig";

type EditorExpandModalProps = {
    isOpen: boolean;
    title: string;
    language: string;
    value: string;
    onChange: (value: string) => void;
    readOnly: boolean;
    onClose: () => void;
    onBeforeMount?: (monaco: Monaco) => void;
};

export function EditorExpandModal({
    isOpen, title, language, value, onChange, readOnly, onClose, onBeforeMount,
}: EditorExpandModalProps) {
    const { t } = useTranslation();
    const closeBtnRef = useRef<HTMLButtonElement>(null);
    const prevFocusRef = useRef<Element | null>(null);
    const titleId = `expand-modal-title-${language}`;

    useEffect(() => {
        if (!isOpen) return;
        prevFocusRef.current = document.activeElement;
        const handleKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", handleKey);
        closeBtnRef.current?.focus();
        return () => {
            document.removeEventListener("keydown", handleKey);
            if (prevFocusRef.current instanceof HTMLElement) {
                prevFocusRef.current.focus();
            }
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <>
            <div className="fixed inset-0 bg-neutral-gray-900/80 z-[51]" onClick={onClose} />
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                className="fixed z-[52] bg-white rounded-lg shadow-2xl flex flex-col overflow-hidden"
                style={{ left: "10%", right: "10%", top: "10vh", height: "80vh" }}
            >
                <div className="flex items-center justify-between px-4 py-3 border-b border-field-outline flex-shrink-0">
                    <span id={titleId} className="text-sm font-semibold text-secondary-gray">{title}</span>
                    <button
                        ref={closeBtnRef}
                        onClick={onClose}
                        className="text-secondary-gray hover:text-off-black transition-colors"
                        aria-label={t("Close")}
                        type="button"
                    >
                        <Icon icon={ic_clear} size={18} />
                    </button>
                </div>
                <div className="flex-1 overflow-hidden p-3">
                    <div className="h-full rounded border border-field-outline overflow-hidden">
                        <Editor
                            height="100%"
                            language={language}
                            value={value}
                            theme="vs"
                            options={MONACO_OPTIONS(readOnly)}
                            beforeMount={onBeforeMount}
                            onChange={(v) => onChange(v ?? "")}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}
